import React, { useState, useEffect } from 'react';
import { View, Text, StyleSheet, ScrollView, TouchableOpacity, Image, Alert, ActivityIndicator, Modal } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { CameraView, Camera } from 'expo-camera';
import QRCode from 'react-native-qrcode-svg';
import { getData, postData } from '../../services/api';
import { H1, H2, Body1, Body2, Caption } from '../../components/common/TextStyles';
import { getImageUrl } from '../../utils/imageUtils';
import { AuthContext } from '../../contexts/AuthContext';

const EventDetailScreen = ({ route, navigation }) => {
  const { eventId } = route.params || {};
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkingIn, setCheckingIn] = useState(false);
  const [rsvper, setRsvper] = useState(false);
  const { user } = React.useContext(AuthContext);
  const [attendees, setAttendees] = useState([]);
  const [attendeesLoading, setAttendeesLoading] = useState(false);
  
  // Scanner state
  const [hasPermission, setHasPermission] = useState(null);
  const [showScanner, setShowScanner] = useState(false);
  const [scanned, setScanned] = useState(false);

  useEffect(() => {
    if (eventId) {
      fetchEventDetails();
    } else {
      Alert.alert('Error', 'No Event ID provided');
      navigation.goBack();
    }
  }, [eventId]);

  const fetchEventDetails = async () => {
    try {
      setLoading(true);
      const data = await getData(`/events/${eventId}`);
      setEvent(data);
      
      // If authorized, fetch attendees
      if (user && (user.role === 'admin' || data.organizer_id === user.id)) {
        fetchAttendees();
      }
    } catch (err) {
      console.error('Failed to load event:', err);
      Alert.alert('Error', 'Failed to load event details');
    } finally {
      setLoading(false);
    }
  };

  const fetchAttendees = async () => {
    try {
      setAttendeesLoading(true);
      const data = await getData(`/events/${eventId}/attendees`);
      setAttendees(data || []);
    } catch (err) {
      console.error('Failed to fetch attendees:', err);
    } finally {
      setAttendeesLoading(false);
    }
  };

  const handleOpenScanner = async () => {
    if (Platform.OS === 'web') {
      Alert.alert('Mobile Required', 'QR scanning is only available on the Youth Connect mobile app. Please open the app on your phone to complete your mission.');
      return;
    }
    const { status } = await Camera.requestCameraPermissionsAsync();
    setHasPermission(status === 'granted');
    if (status === 'granted') {
      setShowScanner(true);
      setScanned(false);
    } else {
      Alert.alert('Permission Denied', 'Camera access is required to scan QR codes');
    }
  };

  const handleBarCodeScanned = async ({ type, data }) => {
    setScanned(true);
    setShowScanner(false);
    try {
      setCheckingIn(true);
      const res = await postData(`/events/${eventId}/verify-qr`, { token: data });
      if (res && res.points_awarded) {
        Alert.alert('Activity Accomplished!', `QR Verified! You earned +${res.points_awarded} XP! 🚀`);
        fetchEventDetails();
      }
    } catch (err) {
      Alert.alert('Verification Failed', err?.response?.data?.error || 'Invalid QR Code for this mission');
    } finally {
      setCheckingIn(false);
    }
  };

  const handleRSVP = async () => {
    try {
      setRsvper(true);
      const res = await postData(`/events/${eventId}/rsvp`, {});
      if (res && res.attendee) {
        Alert.alert('Success', 'Activity Added to your Passbook! 🎟️');
        fetchEventDetails(); // Reload state
      }
    } catch (err) {
      Alert.alert('Oops', 'Could not add to passbook right now.');
    } finally {
      setRsvper(false);
    }
  };

  const handleCheckIn = async () => {
    try {
      setCheckingIn(true);
      const res = await postData(`/events/${eventId}/check-in`, {});
      if (res && res.points_awarded) {
        Alert.alert('Activity Accomplished!', `You earned +${res.points_awarded} XP! 🚀\nNew Total: ${res.new_total} XP`);
        fetchEventDetails();
      }
    } catch (err) {
      Alert.alert('Error', err?.response?.data?.error || 'Failed to check-in');
    } finally {
      setCheckingIn(false);
    }
  };

  if (loading || !event) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color="#6366F1" />
        <Caption style={{marginTop: 10, color: '#6B7280'}}>Decrypting activity details...</Caption>
      </View>
    );
  }

  const formatDateFull = (d) => new Date(d).toLocaleDateString('en-US', { weekday: 'short', month: 'long', day: 'numeric', year: 'numeric' });
  const dateStr = event.event_end_date && new Date(event.event_end_date).toDateString() !== new Date(event.event_date).toDateString()
    ? `${formatDateFull(event.event_date)} - ${formatDateFull(event.event_end_date)}`
    : formatDateFull(event.event_date);
    
  const formatTime = (d) => new Date(d).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  const timeStr = event.start_time && event.end_time 
    ? `${formatTime(event.start_time)} - ${formatTime(event.end_time)}`
    : formatTime(event.event_date);

  const isGoing = event.user_attending;

  return (
    <View style={styles.container}>
      <ScrollView showsVerticalScrollIndicator={false} contentContainerStyle={styles.scrollContent}>
        {/* Hero Section */}
        <View style={styles.heroWrapper}>
           {event.image_url ? (
             <Image source={{ uri: getImageUrl(event.image_url) }} style={styles.heroBg} />
           ) : (
             <View style={[styles.heroBg, { backgroundColor: '#EEF2FF', alignItems: 'center', justifyContent: 'center' }]}>
               <Ionicons name="calendar" size={48} color="#C7D2FE" />
             </View>
           )}
        </View>

        <View style={styles.bodySection}>
            {/* Header Block */}
            <View style={styles.headerBlock}>
                <View style={styles.vibePill}>
                    <Caption style={styles.vibeText}>{event.vibe}</Caption>
                </View>
                <H1 style={styles.title}>{event.title}</H1>
                <View style={styles.metaRow}>
                    <View style={styles.metaBadge}>
                        <Ionicons name="people" size={14} color="#6366F1" />
                        <Caption style={styles.metaText}>{event.total_attendees} Going</Caption>
                    </View>
                    <View style={[styles.metaBadge, styles.metaBadgeXP]}>
                        <Ionicons name="star" size={14} color="#D97706" />
                        <Caption style={styles.metaTextXP}>+{event.points_reward} XP Drop</Caption>
                    </View>
                </View>
            </View>

            {/* Mission Critical Info */}
            <View style={styles.infoCardsWrap}>
               <View style={styles.infoCard}>
                  <View style={styles.iconCircle}>
                     <Ionicons name="time-outline" size={18} color="#6366F1" />
                  </View>
                  <View style={{flex: 1}}>
                     <Body2 style={styles.infoTitle}>Date & Time</Body2>
                     <Caption style={styles.infoSub}>{dateStr} • {timeStr}</Caption>
                  </View>
               </View>

               <View style={styles.infoCard}>
                  <View style={[styles.iconCircle, { backgroundColor: '#D1FAE5' }]}>
                     <Ionicons name={event.type === 'virtual' ? 'globe-outline' : 'location-outline'} size={18} color="#10B981" />
                  </View>
                  <View style={{flex: 1}}>
                     <Body2 style={styles.infoTitle}>{event.type === 'virtual' ? 'Virtual Location' : 'Location'}</Body2>
                     <Caption style={styles.infoSub} numberOfLines={2}>
                        {event.location_name || event.virtual_link || 'Location TBA'}
                     </Caption>
                  </View>
               </View>
            </View>

            {/* Organizer Block */}
            {event.organizer && (
                <View style={styles.organizerBlock}>
                   <Image 
                     source={{ uri: getImageUrl(event.organizer.profileImageUrl) || 'https://ui-avatars.com/api/?name=YC' }}
                     style={styles.orgAvatar}
                   />
                   <View style={{flex: 1}}>
                       <Body2 style={styles.orgName}>{event.organizer.name}</Body2>
                       <Caption style={styles.orgRole}>{event.organizer.isMentor ? `Mentor • ${event.organizer.mentorType}` : 'Event Organizer'}</Caption>
                   </View>
                </View>
            )}

            {/* The Brief (Description) */}
            <View style={styles.missionBrief}>
                <H2 style={styles.briefHeading}>Activity Briefing</H2>
                <Body1 style={styles.briefText}>{event.description || 'No briefing details provided yet. Show up and find out!'}</Body1>
            </View>

            {/* QR Code for Admins */}
            {(user?.role === 'admin' || event?.organizer_id === user?.id) && event.qr_code_token && (
              <View style={styles.qrSection}>
                <H2 style={styles.briefHeading}>Activity Entry QR</H2>
                <Caption style={{ marginBottom: 15, color: '#64748B' }}>Show this to attendees at the venue to verify their visit.</Caption>
                <View style={styles.qrContainer}>
                  <QRCode
                    value={event.qr_code_token}
                    size={160}
                    color="#000000"
                    backgroundColor="#FFFFFF"
                  />
                </View>
              </View>
            )}

            {/* Event Registry (Authorized Only) */}
            {(user?.role === 'admin' || event?.organizer_id === user?.id) && (
              <View style={styles.registrySection}>
                <View style={styles.registryHeader}>
                  <H2 style={styles.briefHeading}>Activity Registry</H2>
                  <View style={styles.registryCountBadge}>
                    <Caption style={styles.registryCountText}>{attendees.length} Joined</Caption>
                  </View>
                </View>
                
                {attendeesLoading ? (
                  <ActivityIndicator size="small" color="#6366F1" style={{ alignSelf: 'flex-start', marginVertical: 10 }} />
                ) : attendees.length > 0 ? (
                  <View style={styles.attendeesContainer}>
                    {attendees.slice(0, 5).map((a, idx) => (
                      <View key={a.id || idx} style={styles.attendeeMiniRow}>
                         <Image 
                           source={{ uri: getImageUrl(a.user?.profileImageUrl) || 'https://ui-avatars.com/api/?name=' + (a.user?.name || 'U') }}
                           style={styles.attendeeAvatar} 
                         />
                         <View>
                           <Body2 style={styles.attendeeName}>{a.user?.name}</Body2>
                           <Caption style={styles.attendeeEmail}>{a.user?.email}</Caption>
                         </View>
                      </View>
                    ))}
                    {attendees.length > 5 && (
                      <TouchableOpacity 
                        style={styles.viewRegistryBtn}
                        onPress={() => Alert.alert('Full Registry', 'You can view and export the complete list from the Admin Management portal.')}
                      >
                        <Caption style={styles.viewRegistryText}>+ {attendees.length - 5} more users</Caption>
                        <Ionicons name="chevron-forward" size={12} color="#6366F1" />
                      </TouchableOpacity>
                    )}
                  </View>
                ) : (
                  <Caption style={styles.emptyRegistryText}>No users have joined yet.</Caption>
                )}
              </View>
            )}

        </View>
      </ScrollView>

      {/* Action Footer */}
      <View style={styles.actionFooter}>
         {!isGoing ? (
            <TouchableOpacity 
              style={styles.primaryBtn}
              onPress={handleRSVP}
              disabled={rsvper}
            >
               <Ionicons name="wallet-outline" size={20} color="#FFFFFF" style={{marginRight: 8}}/>
               <H2 style={styles.btnText}>{rsvper ? 'Adding...' : 'Add to Passbook'}</H2>
            </TouchableOpacity>
          ) : (
            <View style={styles.attendingRow}>
               <View style={styles.attendingStatus}>
                   <Ionicons name="checkmark-circle" size={20} color="#10B981" />
                   <H2 style={[styles.btnText, {color: '#10B981', marginLeft: 6}]}>{event.user_checked_in ? 'Completed' : 'Added to Pass'}</H2>
               </View>
               {!event.user_checked_in && (
                 <TouchableOpacity 
                    style={styles.checkInBtn}
                    onPress={handleOpenScanner}
                    disabled={checkingIn}
                 >
                     <Ionicons name="scan-outline" size={16} color="#FFFFFF" style={{marginRight: 6}} />
                     <Text style={styles.checkInText}>{checkingIn ? '...' : 'Scan QR'}</Text>
                 </TouchableOpacity>
               )}
            </View>
         )}
      </View>

      {/* Barcode Scanner Modal */}
      <Modal visible={showScanner} animationType="slide">
        <View style={styles.scannerWrapper}>
          <CameraView
            onBarcodeScanned={scanned ? undefined : handleBarCodeScanned}
            barcodeScannerSettings={{
              barcodeTypes: ['qr'],
            }}
            style={StyleSheet.absoluteFillObject}
          />
          <View style={styles.scannerOverlay}>
            <View style={styles.scanFrame} />
            <Text style={styles.scanText}>Scan Activity QR Code</Text>
            <TouchableOpacity 
              style={styles.closeScanner} 
              onPress={() => setShowScanner(false)}
            >
              <Ionicons name="close-circle" size={48} color="#FFFFFF" />
            </TouchableOpacity>
          </View>
        </View>
      </Modal>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#FFFFFF',
  },
  loadingContainer: {
    flex: 1,
    backgroundColor: '#FFFFFF',
    alignItems: 'center',
    justifyContent: 'center'
  },
  scrollContent: {
    paddingBottom: 100, // accommodate footer
  },
  heroWrapper: {
    height: 220,
    width: '100%',
  },
  heroBg: {
    width: '100%',
    height: '100%'
  },
  bodySection: {
    padding: 16,
    backgroundColor: '#FFFFFF',
    borderTopLeftRadius: 24,
    borderTopRightRadius: 24,
    marginTop: -20, // Overlaps the image
  },
  headerBlock: {
    marginBottom: 20,
  },
  vibePill: {
    backgroundColor: '#EEF2FF',
    alignSelf: 'flex-start',
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 12,
    marginBottom: 10,
  },
  vibeText: {
    color: '#6366F1',
    fontWeight: 'bold',
    textTransform: 'uppercase',
    fontSize: 10,
  },
  title: {
    color: '#111827',
    fontSize: 24,
    marginBottom: 12,
    lineHeight: 30,
  },
  metaRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  metaBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#F3F4F6',
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 8,
    marginRight: 10,
  },
  metaBadgeXP: {
    backgroundColor: '#FEF3C7',
  },
  metaText: {
    color: '#4B5563',
    marginLeft: 6,
    fontWeight: '600',
    fontSize: 12,
  },
  metaTextXP: {
    color: '#D97706',
    marginLeft: 6,
    fontWeight: '700',
    fontSize: 12,
  },
  infoCardsWrap: {
    flexDirection: 'column',
    gap: 12,
    marginBottom: 24,
  },
  infoCard: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#F9FAFB',
    padding: 12,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#E5E7EB',
  },
  iconCircle: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: '#EEF2FF',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 12,
  },
  infoTitle: {
    color: '#374151',
    fontWeight: '700',
    marginBottom: 2,
    fontSize: 13,
  },
  infoSub: {
    color: '#6B7280',
    fontSize: 12,
  },
  organizerBlock: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 12,
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#F3F4F6',
    marginBottom: 24,
  },
  orgAvatar: {
    width: 40,
    height: 40,
    borderRadius: 20,
    marginRight: 12,
    backgroundColor: '#F3F4F6',
  },
  orgName: {
    color: '#111827',
    fontWeight: '700',
    fontSize: 14,
  },
  orgRole: {
    color: '#6366F1',
    fontSize: 12,
    marginTop: 2,
  },
  missionBrief: {
    marginBottom: 20,
  },
  briefHeading: {
    color: '#111827',
    marginBottom: 10,
    fontSize: 18,
  },
  briefText: {
    color: '#4B5563',
    lineHeight: 22,
    fontSize: 14,
  },
  registrySection: {
    marginTop: 15,
    padding: 15,
    backgroundColor: '#F8FAFC',
    borderRadius: 16,
    borderWidth: 1,
    borderColor: '#E2E8F0',
  },
  registryHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  registryCountBadge: {
    backgroundColor: '#EEF2FF',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 8,
  },
  registryCountText: {
    color: '#6366F1',
    fontWeight: '800',
    fontSize: 10,
  },
  attendeesContainer: {
    gap: 10,
  },
  attendeeMiniRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    paddingBottom: 8,
    borderBottomWidth: 1,
    borderBottomColor: '#F1F5F9',
  },
  attendeeAvatar: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: '#E2E8F0',
  },
  attendeeName: {
    fontSize: 13,
    fontWeight: '700',
    color: '#0F172A',
  },
  attendeeEmail: {
    fontSize: 10,
    color: '#64748B',
  },
  viewRegistryBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingTop: 5,
    gap: 4,
  },
  viewRegistryText: {
    color: '#6366F1',
    fontWeight: '700',
  },
  emptyRegistryText: {
    color: '#94A3B8',
    fontStyle: 'italic',
  },
  qrSection: {
    marginTop: 20,
    padding: 20,
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    borderWidth: 1,
    borderColor: '#E2E8F0',
    alignItems: 'center',
  },
  qrContainer: {
    padding: 15,
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#F1F5F9',
  },
  scannerWrapper: {
    flex: 1,
    backgroundColor: '#000',
  },
  scannerOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  scanFrame: {
    width: 250,
    height: 250,
    borderWidth: 2,
    borderColor: '#6366F1',
    borderRadius: 20,
    backgroundColor: 'transparent',
  },
  scanText: {
    color: '#FFFFFF',
    fontSize: 18,
    fontWeight: '800',
    marginTop: 30,
  },
  closeScanner: {
    position: 'absolute',
    bottom: 50,
  },
  actionFooter: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    padding: 16,
    paddingBottom: 24,
    backgroundColor: '#FFFFFF',
    borderTopWidth: 1,
    borderTopColor: '#F3F4F6',
  },
  primaryBtn: {
    backgroundColor: '#6366F1',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 14,
    borderRadius: 12,
  },
  btnText: {
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: '700',
  },
  attendingRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: '#F9FAFB',
    padding: 12,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#E5E7EB',
  },
  attendingStatus: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  checkInBtn: {
    backgroundColor: '#10B981',
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 8,
  },
  checkInText: {
    color: '#FFFFFF',
    fontWeight: 'bold',
    fontSize: 13,
  }
});

export default EventDetailScreen;
